"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function InsightsGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-80 bg-muted/20 animate-pulse border-t-4 border-muted" />)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-muted-foreground font-serif">
        Unable to load industry insights at this time.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground font-serif">
        No insights have been published yet.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
      {posts.map((post, i) => {
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Analysis";
      const date = post.date ? new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : "";
      return <Card key={post.id} data-index={i} className="flex flex-col h-full rounded-none border-x-0 border-b-0 border-t-4 border-t-secondary bg-transparent shadow-none hover:bg-muted/5 transition-colors duration-300">
            <CardHeader className="px-0 pt-8 pb-6">
              <Badge variant="secondary" className="w-fit rounded-none uppercase tracking-[0.2em] text-[10px] font-bold mb-6 bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 border-none px-3 py-1">
                {category}
              </Badge>
              <CardTitle className="font-serif text-2xl md:text-3xl leading-tight text-foreground">
                <Link to={post.link || "#"} className="hover:text-secondary transition-colors duration-300 decoration-1 underline-offset-4 hover:underline">
                  {post.title}
                </Link>
              </CardTitle>
              {post.featuredImage && <div className="mt-6 w-full h-48 bg-cover bg-center" style={{
            backgroundImage: `url(${post.featuredImage})`
          }} />}
            </CardHeader>
            
            <CardContent className="px-0 flex-grow">
              {post.excerpt && <div className="text-muted-foreground text-sm leading-relaxed line-clamp-4 font-sans" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </CardContent>
            
            <CardFooter className="px-0 pb-8 pt-6 flex justify-between items-center border-b border-border/50 mt-auto">
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-mono">
                {date}
              </span>
              <Link to={post.link || "#"} className="text-sm font-medium flex items-center gap-2 text-foreground hover:text-secondary transition-colors group uppercase tracking-wider text-[11px]">
                Read Article 
                <ArrowRight className="size-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function IndustryInsights() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="industry-insights" className="bg-background py-24 md:py-32 border-t border-border/40">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-foreground mb-6 tracking-tight">
            Industry Insights
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed">
            Perspectives and analysis on global tourism development, EPCO methodologies, and market trends from AOTO's leadership.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <InsightsGrid />
        </PostsProvider>
      </div>
    </section>;
}